import { em } from 'polished'
import PropTypes from 'prop-types'
import React from 'react'

import TextWithPlus from 'components/_special/TextWithPlus'
import ButtonScrollDown from 'components/ButtonScrollDown'
import List from 'components/List'
import { RU } from 'constants/countries'
import { GOODS_TYPES } from 'constants/sales'
import AddAndGoToCart from 'containers/AddAndGoToCart'
import { LocalizationContext } from 'contexts/Localization'
import { ResizeContext } from 'contexts/Resize'
import { Col, Row } from 'styles/components/layout'
import { H1Small } from 'styles/components/typography'
import { getActiveDiscount } from 'utils/sales/discounts'

import DAboutFirstBlockEuroBox from './components/DAboutFirstBlockEuroBox'
import DAboutFirstBlockRussianBox from './components/DAboutFirstBlockRussianBox'
import {
  ColStyled,
  CostContainerSaleStyled,
  CostWrapper,
  FirstBlockWrapperStyled,
  ListStyled,
  ListWrapper,
  RowStyled,
  BlackFridayLabel,
  DiscountLabel,
  LabelsWrapper,
  LabelText,
  SalesContainer,
  ButtonInner,
  Decorations,
  DynamicImageStyled,
} from './styles'
const { discounts } = process.env.localization

const theme = {
  regular: {
    bg: 'lilac',
    button: 'green',
    textButton: 'white',
  },
  blackFriday: {
    bg: 'black',
    button: 'white',
    textButton: 'black',
    labelBg: 'yellow',
    labelText: 'black',
    markColor: 'grass',
  },
  newYear: {
    bg: 'tePapaGreen',
    button: 'white',
    textButton: 'black',
    labelBg: 'monza',
    labelText: 'white',
    decoratingImg: 'dna_newYear',
    markColor: 'yellow',
  },
}

/**
 * DAboutFirstBlock
 *
 * @param {object} props - properties of component
 * @returns {node} DAboutFirstBlock
 */
const DAboutFirstBlock = ({ data, secondBlockRef }) => {
  const { locale, paths, dict, sales } = React.useContext(LocalizationContext)
  const { mobile } = React.useContext(ResizeContext)

  const discountTheme = React.useMemo(() => {
    const { theme } = getActiveDiscount(discounts, locale) || {}
    return theme
  }, [locale])

  const { discount } = sales?.goods
    ? sales.goods[GOODS_TYPES.dna]
    : { discount: 0 }

  const listContent = React.useMemo(
    () =>
      mobile
        ? dict.kits[GOODS_TYPES.dna].list_mob.filter(item => !!item)
        : dict.kits[GOODS_TYPES.dna].list.filter(item => !!item),
    [mobile, dict],
  )

  const isRu = React.useMemo(() => locale === RU, [locale])
  const isZeroPrice =
    sales?.goods?.[GOODS_TYPES.dna]?.currentPrice === 0 ||
    sales?.goods?.[GOODS_TYPES.dna]?.priceWebsite === 0 ||
    sales?.goods?.[GOODS_TYPES.dna]?.price === 0

  const colorTheme = discountTheme ? theme[discountTheme] : theme.regular
  return (
    <FirstBlockWrapperStyled
      bg={colorTheme.bg}
      isExtraBottomIndent={!!discountTheme}
    >
      <RowStyled>
        <ColStyled flexGrow={{ xs: 1 }} textAlign={{ xs: 'center' }}>
          <H1Small dangerouslySetInnerHTML={{ __html: data.title }} />
        </ColStyled>
      </RowStyled>

      <RowStyled>
        <ColStyled width={{ xs: 1, md: 6 / 12 }} pt={{ md: em(20) }}>
          {isRu ? (
            <DAboutFirstBlockRussianBox
              discount={discountTheme ? discount : 0}
              labelTheme={{
                bg: colorTheme.labelBg,
                text: colorTheme.labelText,
              }}
              {...{ path: `${paths.cdn_global}/boxes` }}
            />
          ) : (
            <DAboutFirstBlockEuroBox
              {...{ path: `${paths.cdn_global}/boxes` }}
            />
          )}
          {!!colorTheme.decoratingImg && (
            <Decorations>
              <DynamicImageStyled
                image={`${paths.shared}/decorations/${colorTheme.decoratingImg}`}
                imageType="png"
                imageSizes={{ xs: 260, md: 287, lg: 470 }}
                fitOption="contain"
                namesByBreakpoints
                alt={'Decorations'}
              />
            </Decorations>
          )}
        </ColStyled>

        <Col width={{ xs: 1, md: 6 / 12 }}>
          <Row>
            <ColStyled
              width={{ xs: 1 }}
              order={{ xs: isRu ? 0 : 1, md: 0 }}
              pt={{ xs: em(20), md: 0 }}
              textAlign={{ xs: 'center', md: 'left' }}
            >
              <ListWrapper>
                <ListStyled
                  markColor={colorTheme.markColor}
                  textColor="white"
                  leftAlignment="text"
                >
                  {listContent.map((item, index) => (
                    <List.Item
                      key={index}
                      bulletColor={isRu ? 'white' : 'green'}
                    >
                      {item}
                    </List.Item>
                  ))}
                </ListStyled>
                <TextWithPlus
                  text={dict.kits[GOODS_TYPES.dna].consultation_free}
                />
              </ListWrapper>
            </ColStyled>

            <ColStyled
              width={{ xs: 1 }}
              order={{ xs: isRu ? 1 : 0, md: 0 }}
              pt={{ xs: em(32) }}
            >
              <SalesContainer>
                {!!discountTheme && discount > 0 && !isZeroPrice && (
                  <LabelsWrapper>
                    <DiscountLabel
                      bg={colorTheme.labelBg}
                      textColor={colorTheme.labelText}
                    >
                      <LabelText
                        dangerouslySetInnerHTML={{ __html: `-${discount}%` }}
                      />
                    </DiscountLabel>
                    <BlackFridayLabel>
                      <LabelText
                        dangerouslySetInnerHTML={{
                          __html: dict[discountTheme],
                        }}
                      />
                    </BlackFridayLabel>
                  </LabelsWrapper>
                )}
                <CostWrapper isMobileLeftAlign={!!discountTheme}>
                  {!isZeroPrice && (
                    <CostContainerSaleStyled
                      productType={GOODS_TYPES.dna}
                      isWhite={true}
                      isMobileCenter={!discountTheme}
                      crossedSize={mobile ? 'base' : 'medium'}
                    />
                  )}
                  <AddAndGoToCart
                    goods={[
                      {
                        type: GOODS_TYPES.dna,
                        isSubscription: false,
                      },
                    ]}
                    buttonProps={{
                      bgClassName: `bg--${colorTheme.button}`,
                      textClassName: `text--${colorTheme.textButton}`,
                      withTopMarginOnMobile: true,
                      isDisabled: isZeroPrice
                    }}
                    forceMultipleAdd={true}
                  >
                    <ButtonInner
                      as="span"
                      dangerouslySetInnerHTML={{
                        __html: sales.withoutSales || isZeroPrice
                          ? dict.buttons.out_of_stock
                          : isRu
                          ? dict.buttons.buy
                          : dict.buttons.add_to_cart,
                      }}
                    />
                  </AddAndGoToCart>
                </CostWrapper>
              </SalesContainer>
            </ColStyled>
          </Row>
        </Col>
      </RowStyled>

      {!discountTheme && (
        <Row>
          <ColStyled width={{ xs: 1 }} pt={{ xs: em(40) }}>
            {!isRu && (
              <ButtonScrollDown
                title={data.go_to_site}
                isLightColor={true}
                refToScroll={secondBlockRef}
              />
            )}
          </ColStyled>
        </Row>
      )}
    </FirstBlockWrapperStyled>
  )
}

DAboutFirstBlock.propTypes = {
  data: PropTypes.object.isRequired,
  secondBlockRef: PropTypes.object.isRequired,
}

DAboutFirstBlock.defaultProps = {}

export default DAboutFirstBlock
